import { Button, Flex, Text } from '@chakra-ui/react';

interface PaginationProps {
  page: number; // 1-based
  pageCount: number;
  onChange: (page: number) => void;
  label?: string;
}

const range = (from: number, to: number) =>
  Array.from({ length: to - from + 1 }, (_, i) => from + i);

/** DS pagination (design-guidelines.md §4). Simple windowed pager. */
export const Pagination = ({ page, pageCount, onChange, label }: PaginationProps) => {
  if (pageCount <= 1) return null;
  const start = Math.max(1, Math.min(page - 2, pageCount - 4));
  const end = Math.min(pageCount, start + 4);
  const pages = range(start, end);

  const btn = (p: number, active: boolean) => (
    <Button
      key={p}
      minW='34px'
      h='34px'
      px={2}
      color={active ? 'primary.on' : 'text.default'}
      fontSize='sm'
      fontWeight={500}
      bg={active ? 'primary.solid' : 'bg.surface'}
      borderWidth='1px'
      borderColor={active ? 'primary.solid' : 'border.default'}
      borderRadius='sm'
      _hover={active ? {} : { bg: 'bg.canvas', borderColor: 'border.strong' }}
      onClick={() => onChange(p)}
    >
      {p}
    </Button>
  );

  return (
    <Flex
      align='center'
      justify='space-between'
      wrap='wrap'
      gap={3}
      px={4}
      py={3}
      borderColor='border.default'
      borderTopWidth='1px'
    >
      {label && (
        <Text color='text.muted' fontSize='sm'>
          {label}
        </Text>
      )}
      <Flex align='center' gap={1}>
        <Button
          minW='34px'
          h='34px'
          px={2}
          color='text.default'
          fontSize='sm'
          bg='bg.surface'
          borderWidth='1px'
          borderColor='border.default'
          borderRadius='sm'
          _hover={{ bg: 'bg.canvas', borderColor: 'border.strong' }}
          isDisabled={page <= 1}
          onClick={() => onChange(page - 1)}
        >
          ‹
        </Button>
        {pages.map((p) => btn(p, p === page))}
        <Button
          minW='34px'
          h='34px'
          px={2}
          color='text.default'
          fontSize='sm'
          bg='bg.surface'
          borderWidth='1px'
          borderColor='border.default'
          borderRadius='sm'
          _hover={{ bg: 'bg.canvas', borderColor: 'border.strong' }}
          isDisabled={page >= pageCount}
          onClick={() => onChange(page + 1)}
        >
          ›
        </Button>
      </Flex>
    </Flex>
  );
};

export default Pagination;
